import React, { useEffect, useRef, useState } from "react";
import { CgFileDocument } from "react-icons/cg";
import { MdIntegrationInstructions, MdOutlineLabel, MdOutlineArticle } from "react-icons/md";
import { FaRegFlag } from "react-icons/fa6";
import { RiAccountCircleFill } from "react-icons/ri";
import {
  LuFolderPen,
  LuMail,
  LuMailOpen,
  LuSend,
  LuSettings,
  LuInbox,
  LuChevronUp,
  LuChevronDown,
  LuPlus,
} from "react-icons/lu";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toggleSidebar } from "../redux/sidebarSlice";
import MailLogo from "../assets/SidebarAssets/MailLogo.svg";
import { LuCircleChevronRight, LuCircleChevronLeft, LuArchive } from "react-icons/lu";
import { BREAKPOINTS, SYSTEM_LABELS } from "../utils/emailUtils";
import { AiOutlineClose } from "react-icons/ai";
import { getStoredValue } from "../services/logics";
import SidebarLogo from "../assets/SidebarAssets/SidebarLogo.png";
import { fetchLabels } from "../redux/labelsSlice";
import CreateLabelModal from "./SettingsPageComponents/CreateLabelModal";

// Icon mapping for system labels
const LABEL_ICONS = {
  drafts: LuMailOpen,
  inbox: LuMail,
  sent: LuSend,
  archive: LuArchive,
  outbox: LuInbox,
  "agent-review": FaRegFlag,
};

const Sidebar = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const isSidebarOpen = useSelector(state => state.sidebar.isOpen);
  const { isOpen } = useSelector(state => state.notification);
  const visibilityMap = useSelector(state => state.labelVisibility);
  const { items: customLabels } = useSelector(state => state.labels);
  const isActive = path => location.pathname.includes(path);
  const isMobileScreen = window.matchMedia(
    `${BREAKPOINTS.mobile}, ${BREAKPOINTS.mobileLarge}, ${BREAKPOINTS.tablet}`
  ).matches;
  const sidebarRef = useRef(null);
  const is_super_admin = getStoredValue("is_super_admin");
  const storeid = getStoredValue("storeid");
  const isSuperadminStore = is_super_admin === "True" && storeid !== "undefined";
  const [isCustomLabelsOpen, setIsCustomLabelsOpen] = useState(false);
  const [showCreateLabelModal, setShowCreateLabelModal] = useState(false);

  // Fetch labels on mount
  useEffect(() => {
    dispatch(fetchLabels());
  }, [dispatch]);

  // useEffect(() => {
  //   dispatch(openSidebar());
  // }, [dispatch]);

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };
  const handleLinkClick = () => {
    // Check if the screen matches the mobile breakpoint
    if (isMobileScreen) {
      handleToggleSidebar();
    }
  };
  useEffect(() => {
    if (isSidebarOpen && isMobileScreen) {
      const handleClickOutside = event => {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
          dispatch(toggleSidebar());
        }
      };

      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isSidebarOpen, isMobileScreen, dispatch]);

  const textStyles = "text-base font-semibold";

  const getPaddingTop = () => {
    if (location.pathname.includes("document")) {
      // Apply document-specific padding rules
      if (isOpen && isSuperadminStore) {
        return "pt-[60px] md:pt-[90px]";
      } else if (isOpen) {
        return "pt-[10px] md:pt-[40px]";
      } else if (isSuperadminStore) {
        return "pt-[20px] md:pt-[60px]";
      } else {
        return "pt-[10px] md:pt-[24px]";
      }
    } else {
      // Apply padding only based on isSuperadminStore for other pages
      return isSuperadminStore ? "pt-[20px] md:pt-[60px]" : "pt-[10px] md:pt-[24px]";
    }
  };
  return (
    <>
      {/* Sidebar overlay */}
      {isSidebarOpen && isMobileScreen && (
        <div
          className="fixed inset-0  bg-black opacity-20 backdrop-blur-sm z-20"
          onClick={handleToggleSidebar} // Clicking outside (on overlay) closes sidebar
        />
      )}

      <div
        ref={sidebarRef}
        className={`fixed top-[-10px] z-30 lg:z-10 ${
          isSidebarOpen ? "lg:w-[216px] xl:w-[236px] w-[220px]" : "w-0 lg:w-[80px] lg:block hidden"
        } bg-[#F3F3F3] text-white transition-all ease-in-out duration-300 flex flex-col justify-between lg:flex-shrink-0`}
        style={{
          background: "#0B0C16",
          border: "1.15px solid",
          borderImageSource: "linear-gradient(180deg, #0E151C 0%, #21905F 100%)",
          height: "calc(100vh + 10px)", // Adjust height to compensate for the top offset
        }}
      >
        {/* Navigation and Menu */}
        <div className="flex flex-col h-full overflow-hidden">
          <div className="lg:hidden flex justify-end mt-[12px] mr-1 flex-shrink-0">
            <button className="text-[#2B2E3A] items-center" onClick={handleToggleSidebar}>
              <AiOutlineClose size={16} className="cursor-pointer text-[#D7FDED] hover:text-gray-400" />
            </button>
          </div>
          <div className={`${getPaddingTop()} mb-[5px] lg:mb-[10px] flex-shrink-0`}>
            <div className="flex items-start pt-[2px] justify-start w-full transition-all duration-300">
              <img
                src={isSidebarOpen ? MailLogo : SidebarLogo}
                alt="Logo"
                className={`transition-all duration-300 ease-in-out ${
                  isSidebarOpen ? "w-[250px] h-[80px] mr-12  " : "w-[60px] h-[60px] mx-1"
                }`}
              />
            </div>
          </div>

          <ul className="lg:space-y-3 space-y-8 flex-1 overflow-y-auto pb-20 lg:pb-16 hide-scrollbar">
            <li className={`${isActive("/dashboard") ? "border-r-2 border-primary-700 rounded-sm" : ""}`}>
              <Link
                to="/dashboard"
                className={`flex items-center px-3 py-[10px] space-x-2 ml-2 mr-2 ${
                  isActive("/dashboard")
                    ? " bg-opacity-10 rounded-full text-[#39F6A3] relative"
                    : "text-[#7E7E7E] rounded-full "
                }`}
                onClick={handleLinkClick}
              >
                <MdIntegrationInstructions size={20} />
                <span className={`${isSidebarOpen ? "block" : "hidden"} text-base font-semibold`}>Dashboard</span>
              </Link>
            </li>
            <li className={`${isActive("/policies-page") ? "border-r-2 border-primary-700 rounded-sm" : ""}`}>
              <Link
                to="/policies-page"
                className={`flex items-center px-3 py-[10px] space-x-2 ml-2 mr-2 ${
                  isActive("/policies-page")
                    ? " bg-opacity-10 rounded-full text-[#39F6A3] relative"
                    : "text-[#7E7E7E] rounded-full "
                }`}
                onClick={handleLinkClick}
              >
                <MdOutlineArticle size={20} />
                <span className={`${isSidebarOpen ? "block" : "hidden"} text-base font-semibold`}>Policies</span>
              </Link>
            </li>
            <li
              className={`${
                isActive("/emails-page") || location.pathname.startsWith("/email-page/detail")
                  ? "border-r-2 border-primary-700 rounded-sm"
                  : ""
              }`}
            >
              <Link
                to="/emails-page?filter=drafts"
                className={`flex items-center px-3 py-[10px] space-x-2 ml-2 mr-2 ${
                  isActive("/emails-page") || location.pathname.startsWith("/email-page/detail")
                    ? " bg-opacity-10 rounded-full text-[#39F6A3] relative"
                    : "text-[#7E7E7E] rounded-full "
                }`}
                onClick={handleLinkClick}
              >
                <LuFolderPen size={20} />
                <span className={`${isSidebarOpen ? "block" : "hidden"} text-base font-semibold`}>Emails</span>
              </Link>
            </li>
            {/* Dropdown Menu */}
            {(isActive("/emails-page") || location.pathname.startsWith("/email-page/detail")) && (
              <ul
                className={`ml-8 mt-2 space-y-2 text-[14px] border-l border-[#868CA2C4]  ${
                  isSidebarOpen ? "block" : "hidden"
                }`}
              >
                {/* System labels */}
                {SYSTEM_LABELS.filter(item => visibilityMap[`system:${item.key}`] !== false).map((item, index) => {
                  const Icon = LABEL_ICONS[item.key];
                  const isFirstItem = index === 0;
                  return (
                    <li
                      key={item.key}
                      className={`group flex items-center cursor-pointer ${isFirstItem ? "pt-1" : ""}`}
                    >
                      <Link
                        to={`/emails-page?filter=${item.filter}`}
                        className={`flex items-center h-[32px] space-x-2 px-3 rounded-md w-full mr-4 ${
                          location.search.includes(`filter=${item.filter}`)
                            ? "font-semibold text-[#39F6A3]"
                            : "text-[#7E7E7E] font-medium"
                        }`}
                      >
                        <Icon className="mr-1 flex-shrink-0" />
                        <span className="truncate">{item.name}</span>
                      </Link>
                    </li>
                  );
                })}

                {/* Custom labels header */}
                <li
                  className="flex items-center justify-between h-[32px] px-3 mr-2 cursor-pointer"
                  onClick={() => setIsCustomLabelsOpen(prev => !prev)}
                >
                  {/* Left: Arrow + Label */}
                  <div className="flex items-center space-x-1">
                    <span className="text-white text-sm font-semibold">Custom labels</span>
                    {isCustomLabelsOpen ? (
                      <LuChevronUp size={16} className="text-white" />
                    ) : (
                      <LuChevronDown size={16} className="text-white" />
                    )}
                  </div>

                  {/* Right: Plus icon */}
                  <LuPlus
                    size={16}
                    className="text-[#39F6A3] hover:scale-110 transition"
                    onClick={e => {
                      e.stopPropagation();
                      setShowCreateLabelModal(true);
                    }}
                  />
                </li>

                {/* Custom labels list */}
                {isCustomLabelsOpen &&
                  customLabels
                    .filter(label => visibilityMap[`custom:${label.id}`] !== false)
                    .map(label => (
                      <li key={label.id} className="group flex items-center cursor-pointer">
                        <Link
                          to={`/emails-page?label=${label.id}`}
                          onClick={handleLinkClick}
                          className={`flex items-center h-[32px] px-3 rounded-md w-full mr-4 ${
                            location.search.includes(`label=${label.id}`)
                              ? "font-semibold text-[#39F6A3]"
                              : "text-[#7E7E7E] font-medium"
                          } hover:text-[#39F6A3] transition`}
                        >
                          <MdOutlineLabel className="mr-2 flex-shrink-0" size={15} />
                          <span className="truncate">{label.name}</span>
                        </Link>
                      </li>
                    ))}
              </ul>
            )}
            <li className={`${isActive("/integration-page") ? "border-r-2 border-primary-700 rounded-sm" : ""}`}>
              <Link
                to="/integration-page"
                className={`flex items-center px-3 py-[10px] space-x-2 ml-2 mr-2 ${
                  isActive("/integration-page")
                    ? " bg-opacity-10 rounded-full text-[#39F6A3] relative"
                    : "text-[#7E7E7E] rounded-full "
                }`}
                onClick={handleLinkClick}
              >
                <MdIntegrationInstructions size={20} />
                <span className={`${isSidebarOpen ? "block" : "hidden"} text-base font-semibold`}>Integrations</span>
              </Link>
            </li>
            <li
              className={`${
                isActive("/templates-page") ||
                location.pathname.startsWith("/template") ||
                location.pathname.startsWith("/create-template")
                  ? "border-r-2 border-primary-700 rounded-sm"
                  : ""
              }`}
            >
              <Link
                to="/templates-page"
                className={`flex items-center px-3 py-[10px] space-x-2 ml-2 mr-2 ${
                  isActive("/templates-page") ||
                  location.pathname.startsWith("/template") ||
                  location.pathname.startsWith("/create-template")
                    ? " bg-opacity-10 rounded-full text-[#39F6A3] relative"
                    : "text-[#7E7E7E] rounded-full "
                }`}
                onClick={handleLinkClick}
              >
                <CgFileDocument size={20} />
                <span className={`${isSidebarOpen ? "block" : "hidden"} text-base font-semibold`}>Templates</span>
              </Link>
            </li>

            {/* Settings Page */}
            <li className={`${isActive("/settings-page") ? "border-r-2 border-primary-700 rounded-sm" : ""}`}>
              <Link
                to="/settings-page/auto-reply"
                className={`flex items-center px-3 py-[10px] space-x-2 ml-2 mr-2 ${
                  isActive("/settings-page")
                    ? "bg-opacity-10 rounded-full text-[#39F6A3] relative"
                    : "text-[#7E7E7E] rounded-full "
                }`}
                onClick={handleLinkClick}
              >
                <LuSettings size={20} />
                <span className={`${isSidebarOpen ? "block" : "hidden"} text-base font-semibold`}>Settings</span>
              </Link>
            </li>

            {/* Dropdown Menu */}

            <li className={`${isActive("/accounts-page") ? "border-r-2 border-primary-700 rounded-sm" : ""}`}>
              <Link
                to="/accounts-page"
                className={`flex items-center px-3 py-[10px] space-x-2 ml-2 mr-2 ${
                  isActive("/accounts-page")
                    ? " bg-opacity-10 rounded-full text-[#39F6A3] relative"
                    : "text-[#7E7E7E] rounded-full "
                }`}
                onClick={handleLinkClick}
              >
                <RiAccountCircleFill size={20} />
                <span className={`${isSidebarOpen ? "block" : "hidden"} text-base font-semibold`}>Accounts</span>
              </Link>
            </li>
          </ul>
        </div>

        <div className="lg:flex lg:flex-grow lg:items-end hidden">
          <div
            className={`absolute bottom-4 ${isSidebarOpen ? "right-5" : "right-1/2 transform translate-x-1/2"}`}
            onClick={handleToggleSidebar}
          >
            {isSidebarOpen ? (
              <LuCircleChevronLeft
                size={38}
                className="cursor-pointer text-[#D7FDED] hover:text-gray-400 transition-all duration-300"
                style={{ strokeWidth: 0.6 }}
              />
            ) : (
              <LuCircleChevronRight
                size={38}
                className="cursor-pointer text-[#D7FDED] hover:text-gray-400 transition-all duration-300"
                style={{ strokeWidth: 0.6 }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Create Label Modal */}
      <CreateLabelModal
        isOpen={showCreateLabelModal}
        onClose={() => setShowCreateLabelModal(false)}
        onSuccess={() => dispatch(fetchLabels())}
      />
    </>
  );
};

export default Sidebar;
